/**
 * ForgotPassword.js
 * Password recovery request form
 * Allows users to request a magic link to sign in without password
 *
 * Renders on the sky (SkyPage) in a pale teal card. Fields, handlers, API call,
 * links and the enumeration-safe success behaviour are unchanged from the
 * white-card version — only the markup's class names and the wrapper moved.
 * Styles come from SkyForm.css.
 *
 * ForgotPassword.css is still imported, deliberately: its `.error-message` and
 * `.success-message` rules are relied on by Admin and MyAccount through the
 * global CSS bundle. See docs/TODO.md for the cleanup list.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../services/api';
import SkyPage from '../LandingSky/SkyPage';
import '../LandingSky/SkyForm.css';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({
    type: null, // 'success' | 'error'
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setStatus({
        type: 'error',
        message: 'Please enter your email address'
      });
      return;
    }

    setIsLoading(true);
    setStatus({ type: null, message: '' });

    try {
      const response = await authService.forgotPassword(email);

      setStatus({
        type: 'success',
        message: response.message || 'If an account exists with this email, you will receive a magic link shortly.'
      });

      // Clear email field after success
      setEmail('');

    } catch (error) {
      console.error('Forgot password error:', error);

      // IMPORTANT: Always show neutral success message for server errors
      // This prevents email enumeration attacks through error responses
      // (Rate limits, server errors, non-existent accounts all show same message)
      setStatus({
        type: 'success',
        message: 'If an account exists with this email, you will receive a magic link shortly.'
      });

      // Clear email field to match success behavior
      setEmail('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SkyPage>
      <main className="sky-form-region">
        <div className="sky-form-spacer-top" />

        <div className="sky-form-card">
          <form onSubmit={handleSubmit} className="sky-form-body">
            <h1 className="sky-form-heading">Forgot Your Password?</h1>

            <p className="sky-form-lead">
              No problem! Enter your email address and we'll send you a magic link to sign in.
            </p>

            {status.type === 'success' && (
              <div className="sky-form-success" role="status">
                <p className="sky-form-status-title">&#10003; Check your email</p>
                <p>{status.message}</p>
                <p>
                  The link will expire in 15 minutes and can only be used once.
                </p>
              </div>
            )}

            {status.type === 'error' && (
              <div className="sky-form-error" role="alert">
                {status.message}
              </div>
            )}

            <div className="sky-form-field">
              <label className="sky-form-label" htmlFor="email">Email Address:</label>
              <input
                className="sky-form-input"
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={isLoading}
                aria-invalid={status.type === 'error' ? 'true' : 'false'}
              />
            </div>

            <button
              type="submit"
              className="sky-form-primary"
              disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Magic Link'}
            </button>

            <hr className="sky-form-divider" />

            <p className="sky-form-note">
              Remember your password?{' '}
              <Link className="sky-form-link" to="/login">Sign in</Link>
            </p>
          </form>
        </div>

        <div className="sky-form-spacer-bottom" />
      </main>
    </SkyPage>
  );
};

export default ForgotPassword;
